import { Avatar } from "@deligoo/ui"
import clsx from "clsx"
import { CSSProperties, memo } from "react"
import { useTranslation } from "react-i18next"
import { batch, shallowEqual, useDispatch } from "react-redux"

import { useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { ContextMenu, openContextMenu } from "@/components/ContextMenu"
import { DriverOverview } from "@/components/DriverOverview"
import { useDriversContextMenuTrigger } from "@/modules/DriverContextMenu"
import { selectCount } from "@/store"
import { selectDriverById } from "@/store/drivers"
import { selectFilteredOrdersByDriverId, selectOrdersIdsByDriverId } from "@/store/orders"
import { setAboutPanel, setFocusedDriver, setFocusedOrder, setShowDriverDetails } from "@/store/ui"
import { Driver, Order } from "@/types"
import { DriverUtils, formatDelay, OrderUtils } from "@/utils"

import cls from "./DriverItem.module.scss"
import { TelegramButton } from "./partials/TelegramButton"

const filterIsOrderAssigned = (order: Order) => OrderUtils.isAssigned(order)

type DriverItemProps = {
  driverId: Driver["id"]
  style: CSSProperties
}

export const DriverItem = memo(({ driverId, style }: DriverItemProps) => {
  const driver = useAppSelector(selectDriverById(driverId), shallowEqual)

  const currentOrdersCount = useAppSelector(
    selectCount(selectFilteredOrdersByDriverId(driverId, filterIsOrderAssigned))
  )

  const totalOrdersCount = useAppSelector(selectCount(selectOrdersIdsByDriverId(driverId)))
  const isFocused = useAppSelector((state) => state.ui.focusedDriverId === driverId)

  const contextMenuRef = useDriversContextMenuTrigger({ driverId })

  const dispatch = useDispatch()

  const { t } = useTranslation()

  if (!driver) return null

  const delayColor = DriverUtils.getDelayColor(driver.late)
  const batteryLevelColor = DriverUtils.getBatteryLevelColor(driver.phone_battery_level)

  return (
    <Card.Section
      className={clsx(cls.driverItem, isFocused && cls.focused)}
      onClick={() => {
        batch(() => {
          dispatch(setAboutPanel(false))
          dispatch(setFocusedOrder(null))
          dispatch(setShowDriverDetails(driver.id))
          dispatch(setFocusedDriver(driver.id))
        })
      }}
      style={style}
      ref={contextMenuRef}
    >
      <div className={cls.content}>
        <div className={cls.avatar}>
          <Avatar
            color={DriverUtils.getAvatarColor(driver)}
            indicator={DriverUtils.getAvatarIndicator(driver)}
            dotColor={DriverUtils.getAvatarDotColor(driver)}
          >
            {driver.name}
          </Avatar>
        </div>

        <div className={cls.info}>
          <div className={cls.header}>
            <span className={cls.driverName}>{driver.name}</span>

            {DriverUtils.isOnline(driver) && driver.phone_battery_level && (
              <span className={`text-small font-weight-bold text-${batteryLevelColor}`}>
                {driver.phone_battery_level}%
              </span>
            )}

            <ContextMenu.Button onClick={openContextMenu} className={cls.menuBtn} />
          </div>

          <span className={cls.subheader}>
            <span className={cls.main}>
              {DriverUtils.isOnline(driver) && (
                <span className={clsx(cls.delay, `text-${delayColor}`)}>{formatDelay(driver.late)}</span>
              )}
              <span>
                {currentOrdersCount} / {t("orders:orders", { count: totalOrdersCount })}
              </span>
            </span>

            <TelegramButton telegramChatLink={driver.telegram_chat_link} />
          </span>

          <DriverOverview driverId={driverId} size="small" />
        </div>
      </div>
    </Card.Section>
  )
})
